import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { TEMPLATE_VAR_REGEX } from 'sprites-types/utils/template-variables'
import type { WorkflowConfig } from 'sprites-types/workflow/core'

/**
 * Extract node IDs from workflow output template variables
 * @param workflowConfig - The workflow configuration
 * @returns Set of node IDs referenced in workflow outputs
 */
export function extractOutputNodeIds(workflowConfig: WorkflowConfig): Set<string> {
  const nodeIds = new Set<string>()

  if (!workflowConfig.outputs) {
    return nodeIds
  }

  for (const outputTemplate of workflowConfig.outputs) {
    const matches = outputTemplate.matchAll(TEMPLATE_VAR_REGEX)
    for (const match of matches) {
      const varPath = match[1]
      const parts = varPath.split('.')
      if (parts.length >= 1) {
        const nodeId = parts[0]
        nodeIds.add(nodeId)
      }
    }
  }

  return nodeIds
}

/**
 * Filter node executions to only include those referenced in workflow outputs
 * @param nodeExecutions - Array of node executions
 * @param workflowConfig - The workflow configuration
 * @returns Filtered array of node executions
 */
export function filterNodeExecutionsForOutputs(
  nodeExecutions: NodeExecution[],
  workflowConfig: WorkflowConfig
): NodeExecution[] {
  const outputNodeIds = extractOutputNodeIds(workflowConfig)

  // If no specific outputs are defined, show all executions
  if (outputNodeIds.size === 0) {
    return nodeExecutions
  }

  // Filter to only include executions for nodes referenced in outputs
  return nodeExecutions.filter((execution) => outputNodeIds.has(execution.nodeId))
}
